/**
 * Constants for the Fortress, Blast, and Warband abilities.
 *
 * Shared between backend (ability executors, combat coordinator, minion
 * model) and frontend (display). Keeping the IDs and values here — instead
 * of inlining magic numbers in each service — guarantees the ability
 * description, the executor behavior, and the model hooks all agree.
 */

// ─── Fortress (Tank, Warrior) — level requirement 2 ────────────────────────

/** Stable ability ID for Fortress. */
export const FORTRESS_ABILITY_ID = 'fortress';

/**
 * Permanent HP gained the moment the ability is acquired (in
 * MinionInstance.applyUpgrade). Because a minion's effective max HP is its
 * current HP at combat start, this also permanently raises its max HP.
 */
export const FORTRESS_HP_BONUS = 15;

// ─── Blast (Mage, Healer) — level requirement 4 ────────────────────────────

/** Stable ability ID for Blast. */
export const BLAST_ABILITY_ID = 'blast';

/**
 * Fraction of the attacker's attack dealt as splash damage to the target's
 * alive neighbors (floored to an integer). 0.5 = 50%.
 */
export const BLAST_SPLASH_FRACTION = 0.5;

// ─── Warband (Bard) — level requirement 1 ──────────────────────────────────

/** Stable ability ID for Warband. */
export const WARBAND_ABILITY_ID = 'warband';

/**
 * Attack bonus granted to ALL friendly combat copies while a Warband minion
 * is alive. Combat-only: combat copies are discarded after combat, and the
 * bonus is removed mid-combat when the Warband minion dies.
 */
export const WARBAND_ATTACK_BONUS = 2;

// ─── Necromancy (Mage, Healer) — level requirement 5 ──────────────────────

/** Stable ability ID for Necromancy. */
export const NECROMANCY_ABILITY_ID = 'necromancy';

/**
 * Base minion ID of the Undead. The definition (UNDEAD_MINION_DEFINITION)
 * lives in testMinions.ts and is NOT part of the default draw pool. An
 * Undead reaches the battlefield via Gravedigger (drawn into a hand, then
 * played) or is summoned temporarily in combat by Necromancy.
 * Every Undead card starts with the Improved ability (IMPROVED_ABILITY_ID).
 */
export const UNDEAD_MINION_ID = 'undead';

/** Base attack of an Undead (before the derived Improved bonus). */
export const UNDEAD_ATTACK = 16;

// ─── Heirloom (Warrior, Bard, Mage) — level requirement 2 ───────────────────

/** Stable ability ID for Heirloom. */
export const HEIRLOOM_ABILITY_ID = 'heirloom';

/**
 * When a Heirloom card is SOLD (from hand or battlefield), a random friendly
 * minion on the owner's battlefield permanently gains the sold card's
 * current attack and HP (a 10/10 Heirloom gives +10 ATK and +10 HP). The
 * effect lives in GameSessionService.sellMinion — the single authoritative
 * sell path (all randomness goes through the injected RandomNumberService).
 * If no friendly minion is on the battlefield, the stats are lost. The
 * transfer is still just a sale: it never counts as a play or a summon (the
 * Undead Improved counter is not affected).
 */

// ─── Teacher (Healer) — level requirement 3 ─────────────────────────────────

/** Stable ability ID for Teacher. */
export const TEACHER_ABILITY_ID = 'teacher';

/**
 * While a Teacher minion is on the battlefield, any PERMANENT stat gain it
 * would receive (upgrade stat boosts, permanent combat buffs such as Buff
 * Vitality, Heirloom transfers) is redirected: EVERY neighboring friendly
 * minion (owner's side, battlefield position ± 1) receives the FULL amount,
 * and the Teacher itself receives nothing. A neighbor that is itself a
 * Teacher does not receive the gain either — it passes it outward to its own
 * other neighbor, NEVER back to the unit it received from (this prevents
 * infinite loops: in a line of Teachers the gain walks outward until it
 * reaches the board edge or the first non-Teacher, which then receives it).
 * A Teacher with no neighbors to pass to keeps the gain for itself.
 * Unaffected: keywords/abilities (a Teacher keeps gained abilities),
 * combat-only buffs (e.g., Warband), and all healing (Heal, Lifesteal,
 * Killer). The effect lives in PlayerState.redirectTeacherStatGain, invoked
 * from PlayerState.upgradeMinion, RoundCoordinator.applyPermanentStatChanges
 * and GameSessionService.sellMinion (Heirloom transfer target).
 */

/** Base HP of an Undead (before the derived Improved bonus). */
export const UNDEAD_HP = 12;

// ─── Greater Heal (Healer, Bard) — level requirement 3 ─────────────────────

/** Stable ability ID for Greater Heal. */
export const GREATER_HEAL_ABILITY_ID = 'greater_heal';

/**
 * Stable ability ID for the original Heal ability (neighbor healing).
 * Greater Heal REPLACES it — see GREATER_HEAL_AMOUNT below.
 */
export const HEAL_ABILITY_ID = 'heal';

/**
 * HP restored to EVERY LIVING friendly minion (owner's side — including the
 * attacker itself) when the Greater Heal owner attacks. Combat copies only
 * (discarded after combat), capped at each target's initial HP — same
 * convention as Heal. Fortress doubles the heal on a target, the owner's
 * Rune of Healing multiplies it (multiplicative with Fortress).
 *
 * Acquisition rule: a minion can only LEARN Greater Heal if it already has
 * the Heal ability; the upgrade removes Heal (abilitiesRemoved: ['heal']),
 * so a minion never holds both.
 */
export const GREATER_HEAL_AMOUNT = 6;

// ─── Grandmaster (Healer, Mage, Blightcaster, Buffer, Runekeeper) — level
//     requirement 5 ────────────────────────────────────────────────────────

/** Stable ability ID for Grandmaster. */
export const GRANDMASTER_ABILITY_ID = 'grandmaster';

/**
 * A Grandmaster minion can NEVER have more than this much HP. The moment
 * the ability is acquired, the minion's HP is adjusted down to this value
 * (MinionInstance.applyUpgrade), and every HP gain afterwards is ignored
 * (MinionInstance.gainHp clamp, combat-copy guards in the Buff Vitality
 * executor and the Health-rune buff in RoundCoordinator). Healing is
 * already a no-op: heal caps use initial HP, which starts at 1.
 *
 * The level-extension effect (+1 max upgrade per Grandmaster ON THE
 * BATTLEFIELD, stacking) is enforced at every level-cap site via
 * PlayerState.getMaxUpgrades — while a Grandmaster is on the board its
 * owner's minions may be upgraded one extra time each; minions that
 * already leveled up KEEP their level when the Grandmaster leaves.
 */
export const GRANDMASTER_MAX_HP = 1;

// ─── Buff Attack (Bard) — level requirement 0 ──────────────────────────────

/** Stable ability ID for Buff Attack. */
export const BUFF_ATTACK_ABILITY_ID = 'buff_attack';

/**
 * Permanent attack gained by the LIVING minion to the LEFT of the owner
 * (battlefield position - 1, owner's side — same "left neighbor" convention
 * as Buff Vitality) when the owner attacks. Persists after combat
 * restoration (permanentStatChanges), and is also applied to the combat
 * copy during the current combat (visible mid-combat via an aura event).
 * Teacher redirection applies (it is a permanent stat gain); Fortress does
 * NOT double it (Fortress only doubles HP effects).
 */
export const BUFF_ATTACK_BONUS = 3;

// ─── Discount (Bard) — level requirement 2 ────────────────────────────────

/** Stable ability ID for Discount. */
export const DISCOUNT_ABILITY_ID = 'discount';

/**
 * Gold reduction applied to the card purchase cost for EVERY living minion
 * with Discount on the owner's battlefield (the effects STACK — two
 * Discounts in play reduce the cost by 2). The cost can never drop below
 * GameConfig.cardPurchaseMinCost. Only minions actually in play count
 * (hand cards have no effect). The reduction is applied in
 * GameSessionService.getCardPurchaseCost — the single authoritative place
 * where the purchase cost is computed.
 */
export const DISCOUNT_COST_REDUCTION = 1;

// ─── Gravedigger (Mage, Healer) — level requirement 3 ─────────────────────

/** Stable ability ID for Gravedigger. */
export const GRAVEDIGGER_ABILITY_ID = 'gravedigger';

/**
 * Gravedigger makes the Undead card drawable: while a minion owning this
 * ability is on the battlefield, UNDEAD_MINION_DEFINITION is added to its
 * owner's draw pool for each round draw (RoundCoordinator). The Undead is
 * NOT in the base pool — Gravedigger is the only way it can be drawn.
 */

// ─── Improved (Undead) — level requirement 0 ───────────────────────────────

/** Stable ability ID for Improved. */
export const IMPROVED_ABILITY_ID = 'improved';

/**
 * Stat bonus granted by Improved: +IMPROVED_STAT_BONUS attack and
 * +IMPROVED_STAT_BONUS HP for every PREVIOUS Undead that the OWNING
 * player has played from their hand or summoned in combat this game.
 * The PER-PLAYER counters (RoundCoordinator.undeadPlayedOrSummonedCountFor
 * (playerId)) are the single source of truth — the buff is constrained to
 * the owning player (another player's played/summoned Undead never count).
 * The bonus is DERIVED — it is never baked into persistent minion stats,
 * so hand cards, battlefield minions and combat copies all show up-to-date
 * stats whenever the owner's counter changes.
 */
export const IMPROVED_STAT_BONUS = 1;

// ─── Explosive shared ID ──────────────────────────────────────────────────

/**
 * Stable ability ID for Explosive. (The ID previously lived only in
 * GameAbilities; it is shared here so the upgrade replacement rules —
 * Exploding REPLACES Explosive, see EXPLODING_ABILITY_ID below — can
 * reference it without a services import.)
 */
export const EXPLOSIVE_ABILITY_ID = 'explosive';

// ─── Exploding (Mage) — level requirement 3 ───────────────────────────────

/** Stable ability ID for Exploding. */
export const EXPLODING_ABILITY_ID = 'exploding';

/**
 * Damage Exploding deals to ALL minions (both sides, every living copy) for
 * EACH friendly death while the Exploding copy is alive — "Explosive
 * triggered by any friendly death". Same flat 3 as Explosive's blast;
 * Fortress minions take double (ability damage convention). A LIVING
 * Exploding copy re-fires for every friendly death it witnesses (the chain
 * can detonate it again and again), and the dead minion's OWN Exploding
 * counts as a contribution too (own-death trigger — a minion dies WITH
 * Exploding, it explodes, no matter how it died).
 *
 * Acquisition rule: a minion can only LEARN Exploding if it already has the
 * Explosive ability; the upgrade removes Explosive (abilitiesRemoved via
 * abilitiesReplacedByUpgrade), so a minion never holds both forms.
 *
 * The effect lives in RoundCoordinator (the per-exchange explosion CHAIN,
 * applyExploding → _runExplosionChain) — the executor is a no-op, same
 * pattern as Necromancy. The chain runs AFTER the OnDeath windows and the
 * sudden-death tick (so deaths from ANY source count — exchange, splash,
 * sudden death, or a blast of the chain itself) and BEFORE the rune death
 * effects / Necromancy scan (so the chain's own kills feed them the same
 * exchange, exactly like Explosive's do). Deaths are resolved in BFS order
 * (A dies → its explosion kills C and E → C explodes → kills F → E → K →
 * F → K → ...); a blast kill that never had its OnDeath window runs it
 * inside the chain, so Explosive and Exploding chain together.
 */
export const EXPLODING_DAMAGE = 3;

// ─── Pyrotechnic (Mage, level 4 — task 198) ──────────────────────────────────
/**
 * PYROTECHNIC (Mage, level 4): "the explosive and exploding keyword deals
 * 3 more damage" — while a Pyrotechnic minion is ALIVE and in play, the
 * Explosive and Exploding blasts of ALL of the owner's minions deal
 * +PYROTECHNIC_BONUS_DAMAGE each, and the effect STACKS: two living
 * Pyrotechnic minions = +6 (3 + 3) extra damage. The count is read live at
 * blast time: a Pyrotechnic that DIED earlier in the combat no longer
 * counts, and the DYING minion itself never counts for its own blast (the
 * OnDeath window / the Exploding trigger run after the corpse is
 * finalized — isAlive is false). The bonus is part of the ability damage,
 * so a
 * Fortress victim doubles it with the rest of the blast.
 */
export const PYROTECHNIC_ABILITY_ID = 'pyrotechnic';
export const PYROTECHNIC_BONUS_DAMAGE = 3;

// ─── LifeBurst (Healer, level 2 — task 206) ────────────────────────────────
//
// When a LifeBurst holder DIES (in combat), EVERY minion on the board —
// yours AND the enemy's — is healed for LIFEBURST_HEAL_AMOUNT HP (capped
// at each minion's combat max HP). The heal lands at the moment of death
// (the OnDeath window — both sides' combat copies; the negative-damage
// heal events stream per target, and a board-wide 'lifeBurst' event
// carries the signature FX: the bright green battlefield flash + the
// green bursts / plus-signs). Non-combat deaths (sold, discarded) never
// trigger it (the OnDeath convention — the executor sees no combat
// copies and no-ops).
export const LIFEBURST_ABILITY_ID = 'life_burst';
export const LIFEBURST_HEAL_AMOUNT = 12;

// ─── Inspired (any class, level 0 — task 207) ──────────────────────────────
//
// A TEMPORARY keyword: it can only be acquired for ONE combat (the Hero
// keyword grants it to friendly minions; a future effect can grant it the
// same way). It is CATALOG-ONLY — no class can learn it through an
// upgrade (the `types: []` convention, like the spell-granted keywords).
//
// Effect: +INSPIRED_STAT_BONUS / +INSPIRED_STAT_BONUS (HP / attack) for
// the rest of the combat. When a minion that ALREADY carries the keyword
// receives it again, the keyword's CURRENT boosting stats are DOUBLED
// (4 → 8 → 16 → …: each further grant doubles the total the keyword
// gives that minion). GRANDMASTER recipients respect the keyword: their
// HP can never exceed 1 (GRANDMASTER_MAX_HP) — the HP portion of the
// boost is ignored, the attack portion lands (the Grandmaster HP-gain
// clamp convention, exactly like the other HP boosts). The stats are
// COMBAT-SCOPED: they live on the combat copies (the session's
// inspiredStats map tracks the per-copy total) and are discarded with
// the copies when combat ends (battlefield restoration).
export const INSPIRED_ABILITY_ID = 'inspired';
export const INSPIRED_STAT_BONUS = 4;

// ─── Hero (Warrior, level 6 — task 207) ────────────────────────────────────
//
// OnKill (ATTACKING kills only — the OnKill convention: a defensive
// counter-kill never triggers it): every time the Hero kills a minion
// with its attack, ALL OTHER living friendly minions receive the
// temporary Inspired keyword (the combat copies gain the stats — see
// INSPIRED_ABILITY_ID). If the Hero kills SEVERAL minions (one per
// exchange, or several in one exchange through chained kills) it grants
// Inspired once PER KILL — a friendly minion granted it twice has its
// Inspired boosting stats doubled (the current total doubles: 2 → 4).
export const HERO_ABILITY_ID = 'hero';

// ─── Shield Breaker (Warrior/Blightcaster) — level requirement 3 ─────────

/**
 * Stable ability ID for Shield Breaker.
 *
 * Effect: when this unit ATTACKS a unit that has the SHIELD keyword
 * (taunt), that unit's Shield is TEMPORARILY REMOVED for the rest of THIS
 * combat (the combat copy loses the 'shield' ability — targeting rules
 * stop forcing attacks at it, exactly like the shield is gone) and
 * RESTORED afterward (the effect lives on the CombatMinionCopy only — the
 * persistent MinionInstance keeps its Shield). The first Shield Breaker
 * attack on a Shielded unit breaks it; while the Shield is gone further
 * attacks do nothing (the shield is already removed). Combat copy only —
 * nothing persists after battle (the Brute/Frozen convention). The red
 * shield-shatter particle burst streams as the dedicated 'shieldBroken'
 * combat event (the FX plays ONLY when a Shield is actually removed).
 */
export const SHIELD_BREAKER_ABILITY_ID = 'shield_breaker';

// ─── DeathFeeder (Tank) — level requirement 5 ─────────────────────────────

/** Stable ability ID for DeathFeeder. */
export const DEATHFEEDER_ABILITY_ID = 'deathfeeder';

/**
 * How many Improved stacks one DeathFeeder feed grants.
 *
 * EVERY TIME a DeathFeeder minion survives taking damage in combat it
 * "feeds": the OWNER's Improved counter
 * (RoundCoordinator.undeadPlayedOrSummonedCountFor(ownerId)) advances by
 * DEATHFEEDER_FEED_STACKS — exactly like the owner playing another Undead
 * from their hand or summoning one in combat (registerUndeadPlayed /
 * registerUndeadSummoned / registerDeathFeederFeed each advance the
 * given player's counter by one as well).
 *
 * The buff is CONSTRAINED TO THE OWNING PLAYER: the feed only advances the
 * owner's counter, so only the owner's minions with Improved (their Undead
 * cards) gain
 * DEATHFEEDER_FEED_STACKS × IMPROVED_STAT_BONUS attack/HP from the feed —
 * in hand and on the battlefield (derived at broadcast time,
 * GameSessionService.improvedBonusFor) and in combat (updateImprovedBuff,
 * applied in place to the owner's living copies). The opponent's Improved
 * minions are untouched. The DeathFeeder tank itself gains NO stats at all.
 *
 * The buff is purely DERIVED from the counter — it is never baked into
 * persistent minion stats, never recorded in permanentStatChanges, and is
 * not doubled by Fortress (the same conventions as the Improved bonus
 * itself). The counter is per-player and monotonic (reset only in
 * startMatch), so feeds accumulate across rounds: every subsequent board /
 * hand broadcast and every subsequent combat copy derives the higher bonus.
 */
export const DEATHFEEDER_FEED_STACKS = 1;

// ─── SoulLink (Tank) — level requirement 3 ────────────────────────────────

/** Stable ability ID for SoulLink. */
export const SOULLINK_ABILITY_ID = 'soul_link';

/**
 * When a SoulLink minion DIES in combat (any death source — combat damage,
 * ability damage, sudden death), a random OTHER living friendly minion gains
 * the deceased's MAX HP (its combat copy's current initialHp at death, so
 * combat-only buffs such as Fortress's +15 and Blessed gains are included)
 * as a TEMPORARY HP buff: the recipient's current HP AND its max HP (heal
 * cap) both rise by the grant for the rest of THIS combat only. The buff is
 * applied to the combat copies in place (never to permanentStatChanges), so
 * battlefield restoration discards it when combat ends.
 *
 * There is no fixed grant value — the amount IS the source's max HP at
 * death. A recipient with Fortress doubles the grant (target-side
 * convention — same as Heal / Buff Vitality); a recipient with Grandmaster
 * can never hold the buff and is excluded from the recipient pool.
 * If no other friendly minion is alive, the soul is lost (Heirloom
 * convention).
 *
 * The effect lives in RoundCoordinator.applySoulLink (the per-exchange
 * SoulLink-death scan, run after Exploding and BEFORE the rune death
 * effects / Necromancy scan — the corpse copy is still in the map at that
 * point, so its max HP is readable). The executor is a no-op, same pattern
 * as Exploding / Necromancy. Resurrect is DEFERRED to after the whole
 * exchange (the CombatSession.deferredResurrects field — "if there is a
 * Resurrect, resolve it last"), so a copy resurrected at the end of the
 * exchange was a corpse at scan time and GRANTS for that death (it DID
 * die); the revival then makes it a fresh living minion for the next
 * exchange (a later death there grants again — per death, like Exploding's
 * per friendly death). Lifesteal / Killer heals do NOT create "never died"
 * cases either: under the no-resurrection rule they only heal SURVIVING
 * copies (a 0-HP copy stays dead and GRANTS like any other corpse), so a
 * lifesteal/killer-healed copy was alive at scan time and never a corpse.
 */

// ─── Master Buff (Bard, Buffer) — level requirement 4 ─────────────────────

/** Stable ability ID for Master Buff. */
export const MASTER_BUFF_ABILITY_ID = 'master_buff';

/**
 * Permanent +HP AND +ATK granted to EACH LIVING neighbor of the Master Buff
 * owner (battlefield position ± 1, owner's side) every time the owner
 * attacks. Both portions are permanent (recorded in permanentStatChanges,
 * applied to the persistent instances after combat restoration — the Buff
 * Vitality / Buff Attack convention) and are also applied to the combat
 * copies in combat, so the buff is in effect from the neighbor's next
 * attack in the same combat (mid-combat ATK changes reach the UI via the
 * OnAttack aura scan, like Buff Attack).
 *
 * Target conventions (same as Buff Vitality / Buff Attack): a neighbor with
 * Fortress doubles the HP portion only (Fortress doubles HP effects, never
 * attack); a neighbor with Grandmaster can never gain HP (the HP portion is
 * ignored for it — the ATK portion still applies, the Rune of Light
 * convention); a neighbor with Teacher redirects both portions to ITS
 * neighbors (the apply-then-redirect convention).
 *
 * Acquisition rule: a minion can only LEARN Master Buff if it ALREADY has
 * BOTH the Buff Vitality AND the Buff Attack abilities; the upgrade removes
 * BOTH (abilitiesRemoved via abilitiesReplacedByUpgrade), so a minion never
 * holds Master Buff together with either of the two it replaces — and once
 * replaced, neither Buff Vitality nor Buff Attack can be re-learned (the
 * two-direction rule, same convention as Greater Heal/Heal and
 * Exploding/Explosive). EXCEPTION: the locked Buffer class STARTS with
 * Master Buff as its base ability (BUFFER_MINION_DEFINITION in
 * testMinions.ts) — the acquisition rule only ever applies to Bards,
 * because a Buffer can never hold the two replaced buffs (they are never
 * offered to a minion that already has Master Buff).
 */
export const MASTER_BUFF_STAT_BONUS = 5;

// ─── WarTribe (Bard) — level requirement 4 ─────────────────────────────────

/** Stable ability ID for WarTribe. */
export const WARTRIBE_ABILITY_ID = 'war_tribe';

/**
 * PERMANENT attack granted to EVERY minion on the owner's battlefield at
 * the start of EACH combat, for every WarTribe minion the owner has in
 * play (the effects STACK — two WarTribe minions give +6, the Warband
 * per-copy stacking convention). "Permanently" follows the Rune of Light
 * convention: the gain goes to the PERSISTENT minion instances BEFORE the
 * pre-combat snapshot (RoundCoordinator.applyWarTribe, called in runRound's
 * combat step), so the snapshot, the combat copies and the post-combat
 * restoration all carry it — every combat adds another permanent +3 per
 * WarTribe minion to the whole board.
 *
 * Teacher redirection applies (it is a permanent stat gain — the gain is
 * applied per minion and then run through redirectTeacherStatGain, the
 * apply-then-redirect convention). Hand minions are not buffed (they do
 * not fight).
 *
 * Acquisition rule: a minion can only LEARN WarTribe if it ALREADY has the
 * Warband ability; the upgrade removes Warband (abilitiesRemoved via
 * abilitiesReplacedByUpgrade), so a minion never holds both forms — and
 * Warband is never re-offered to a WarTribe minion (the two-direction
 * rule, same convention as Greater Heal/Heal and Exploding/Explosive).
 *
 * The executor is a no-op — the effect lives in RoundCoordinator (the
 * combat-step permanent buff), same pattern as Warband's aura.
 */
export const WARTRIBE_ATK_BONUS = 3;

// ─── Holy Shield (Tank, Warrior) — level requirement 3 ───────────────────

/** Stable ability ID for Holy Shield. */
export const HOLY_SHIELD_ABILITY_ID = 'holy_shield';

/**
 * Damage dealt to a Holy Shield minion by an UNDEAD-class unit (the
 * exchange hit AND the counter-attack, Blast splash from an Undead attacker,
 * Explosive/Exploding blasts from an Undead source) is divided by this and
 * ROUNDED UP (an 8-ATK Undead deals 4, a 7-ATK Undead deals 4, a 1-ATK
 * Undead deals 1). Damage from non-Undead sources and sourceless damage
 * (the sudden-death tick) is never reduced.
 *
 * The effect lives in CombatMinionCopy.takeDamage (the single damage choke
 * point) — the damage source's minion class travels with each takeDamage
 * call. There is no executor; the reduction is a damage rule, not an event.
 * When the reduction actually applies, the copy queues an 'holy_shield'
 * trigger (pendingAbilityTriggers) that the RoundCoordinator drains into the
 * combat stream (the client's blue shield wave at the card).
 */
export const HOLY_SHIELD_DAMAGE_DIVISOR = 2;

// ─── Second Chance (all classes — Warrior, Bard, Mage, Healer, Tank,
//     Undead, Blightcaster, Buffer, Runekeeper) — level requirement 6 ──────

/** Stable ability ID for Second Chance. */
export const SECOND_CHANCE_ABILITY_ID = 'second_chance';

/**
 * The FIRST time a Second Chance minion would take LETHAL damage in combat
 * (amount >= current HP, from ANY source — exchange hit, counter-attack,
 * Blast splash, Explosive/Exploding AoE, sudden-death tick — every damage
 * site routes through CombatMinionCopy.takeDamage), the damage is NEGATED
 * BEFORE it is applied and the minion is FULLY HEALED instead (current HP
 * set to the combat copy's initialHp — the max HP at that moment, combat
 * buffs included). The damage is ignored before it lands, so the copy never
 * dies: no death event, no kill credit, no kill gold, no OnKill/OnDeath
 * dispatches, no resurrection observation. Later lethal hits in the same
 * combat apply normally (per-combat charge, the hasKilledInCombat /
 * blessedUsedInCombat convention — copies are rebuilt every round, so the
 * charge resets automatically each combat).
 *
 * ACQUISITION: level requirement 6 — and it can only be learned by leveling
 * PAST the base maximum level, which only exists while a friendly
 * GRANDMASTER minion is on the battlefield (the Grandmaster level-extension
 * effect, PlayerState.getMaxUpgrades). The rule is enforced in the shared
 * eligibility check (isAbilityEligibleForUpgrade, the grandmasterCount
 * context): the minion must be AT or above the base max level, a friendly
 * Grandmaster must be on the battlefield, and the extended upgrade must
 * still be available. There is no executor; the intercept lives in
 * CombatMinionCopy.takeDamage. When it triggers, the copy queues a
 * 'second_chance' trigger (pendingAbilityTriggers) that the
 * RoundCoordinator drains into the combat stream (the client's golden wing
 * burst at the card).
 */

// ─── Blessed (Tank) — level requirement 3 ─────────────────────────────────

/** Stable ability ID for Blessed. */
export const BLESSED_ABILITY_ID = 'blessed';

/**
 * The FIRST time a Blessed minion takes damage in combat — from ANY source
 * (the normal exchange, counter-attacks, Blast splash, Explosive/Exploding
 * AoE, the sudden-death tick — every damage site routes through
 * CombatMinionCopy.takeDamage, the single choke point) — it GAINS that much
 * HP instead of taking the damage (a 5-HP Blessed tank hit for 3 ends the
 * exchange at 8 HP). The gain is a TEMPORARY HP buff: the copy's current HP
 * AND its max HP (heal cap) both rise by the gained amount for the rest of
 * THIS combat only. The per-combat charge lives on the combat copy
 * (blessedUsedInCombat — combat copies are rebuilt every round, so the
 * charge resets automatically each combat, hasKilledInCombat precedent).
 * A Blessed minion that has Fortress doubles the gain (ability-HP-effect
 * convention — fortressHpMultiplier). Non-positive "damage" never consumes
 * the charge. Nothing is recorded in permanentStatChanges — the buff dies
 * with the combat copies.
 */

// ─── RuneKeeper (Runekeeper) — level requirement 1 ────────────────────────

/** Stable ability ID for RuneKeeper. */
export const RUNE_KEEPER_ABILITY_ID = 'rune_keeper';

/**
 * Additional per-game rune purchases granted by EACH RuneKeeper minion on
 * the battlefield (the effect STACKS — two RuneKeepers give +2 to the base
 * cap). The cap is checked at purchase time only (GameSessionService.
 * buyRune, via getEffectiveRuneCap in RunePurchaseRules): a player who
 * bought runes WHILE a RuneKeeper was in play KEEPS them when it later
 * leaves the board — only FURTHER purchases are blocked (the cap simply
 * shrinks back down, e.g. 4 owned vs cap 3). Only minions actually IN PLAY
 * count (hand cards have no effect — the Discount convention).
 *
 * ACQUIRED BY: the Runekeeper class — it is the RUNEKEEPER_MINION_DEFINITION
 * base ability (testMinions.ts); the definition's `types` is exactly
 * ['runekeeper'], so no other class can ever be offered it. The executor is
 * a no-op (passive convention — the purchase path reads the battlefield).
 */
export const RUNE_KEEPER_CAP_BONUS = 1;

// ─── RuneFeast (Runekeeper) — level requirement 3 ──────────────────────────

/** Stable ability ID for RuneFeast. */
export const RUNE_FEAST_ABILITY_ID = 'rune_feast';

/**
 * Fraction of the base rune price removed in the rune shop while at least
 * one RuneFeast minion is on the owner's battlefield (0.25 = 25% off; the
 * discounted price is FLOORED — 10g → 7g, 15g → 11g, 20g → 15g). The
 * discount does NOT stack: any number of RuneFeasts gives the same flat
 * 25%. Only minions actually IN PLAY count (hand cards have no effect —
 * the Discount convention).
 *
 * ACQUIRED BY: the Runekeeper class (level requirement 3) — the
 * definition's `types` is exactly ['runekeeper'], so no other class can
 * ever be offered it. The executor is a no-op (passive convention —
 * the purchase path reads the battlefield).
 */
export const RUNE_FEAST_DISCOUNT_FRACTION = 0.25;

// ─── Locked (not yet obtainable) classes: Blightcaster, Buffer, Runekeeper ─
//
// Three card classes that exist in the shared model but are EXCLUDED from
// the draw pool (like the Undead card — see UNDEAD_MINION_ID): there is no
// acquisition path yet (no draw, buy, summon or replacement source), so
// none of them can be drawn, bought or played. Their definitions
// (BLIGHTCASTER_MINION_DEFINITION, BUFFER_MINION_DEFINITION,
// RUNEKEEPER_MINION_DEFINITION) live in testMinions.ts — exported but NOT
// part of the `testMinions` base pool.
//
//   Blightcaster: 16 HP / 1 ATK, no base abilities, slime-green class color.
//   Buffer:       15 HP / 2 ATK, starts with the Master Buff ability.
//   Runekeeper:   10 HP / 1 ATK, starts with the RuneKeeper ability and can
//                 learn Shield (level 1) and RuneFeast (level 3).
//   All three can learn Grandmaster (level 5) and Second Chance (level 6 +
//   the Grandmaster battlefield rule — the shared eligibility check, class
//   agnostic).

/** Stable base minion ID of the Blightcaster. */
export const BLIGHTCASTER_MINION_ID = 'blightcaster';

/** Base HP of a Blightcaster. */
export const BLIGHTCASTER_HP = 16;

/** Base attack of a Blightcaster. */
export const BLIGHTCASTER_ATTACK = 1;

/** Stable base minion ID of the Buffer. */
export const BUFFER_MINION_ID = 'buffer';

/** Base HP of a Buffer. */
export const BUFFER_HP = 15;

/** Base attack of a Buffer. */
export const BUFFER_ATTACK = 2;

/** Stable base minion ID of the Runekeeper. */
export const RUNEKEEPER_MINION_ID = 'runekeeper';

/** Base HP of a Runekeeper. */
export const RUNEKEEPER_HP = 10;

/** Base attack of a Runekeeper. */
export const RUNEKEEPER_ATTACK = 1;

// ─── Blightcaster abilities (Task: new Blightcaster kit) ──────────────────
// Four abilities of the locked Blightcaster class (the class is only on the
// board via the transform system — see TransformDefinitions). All four are
// combat-scoped effects: the mechanics live in CombatMinionCopy.takeDamage
// (Mark / Shared Pain) and in the RoundCoordinator (Toxic Cloud tick / the
// Shared Pain drain), the OnAttack executors (Blunt / Mark) — the same
// distribution as the other combat abilities.

// ─── Toxic Cloud (Blightcaster) — level requirement 3 ─────────────────────

/** Stable ability ID for Toxic Cloud. */
export const TOXIC_CLOUD_ABILITY_ID = 'toxic_cloud';

/**
 * Damage dealt to EVERY LIVING enemy minion at the start of EACH of the
 * enemy's attack turns while a Toxic Cloud holder is alive (the holder's
 * enemy side — the side whose turn it is). The clouds STACK: each living
 * holder adds TOXIC_CLOUD_DAMAGE (two holders = 4) — but the cloud triggers
 * only ONCE per turn (a single damage instance per enemy minion, one green
 * cloud FX over the whole enemy battlefield; the effect lives in
 * RoundCoordinator.applyToxicCloudTick, which runs at the top of the combat
 * loop before the attacker is chosen, so a minion the cloud kills simply
 * never attacks and a wiped side ends the combat).
 *
 * The damage routes through CombatMinionCopy.takeDamage like every other
 * damage site (Mark adds its +3, Blessed can convert, Second Chance can
 * negate, a Shared Pain holder accumulates it). Ability-damage convention:
 * simultaneous damage/death events + per-user kill credit for enemy
 * non-token victims + kill gold for the kills via the centralized
 * death-gold rule (RoundCoordinator._awardDeathGold — the cloud is the
 * enemy side's doing, so each kill pays the holder's owner).
 */
export const TOXIC_CLOUD_DAMAGE = 2;

// ─── Blunt (Blightcaster) — level requirement 1 ───────────────────────────

/** Stable ability ID for Blunt. */
export const BLUNT_ABILITY_ID = 'blunt';

/**
 * Attack reduction applied to EVERY LIVING ENEMY combat copy when the Blunt
 * owner attacks (each attack dulls again — stacking per attack, floored at
 * 0). TEMPORARY by construction: it is applied to the combat copies only
 * (never to permanentStatChanges), so battlefield restoration discards it —
 * "the reduction is only temporary and doesn't persist after battle". The
 * executor is real (OnAttack — see bluntExecutor in GameAbilities); the
 * mid-combat ATK change reaches the UI via the defender-side aura event the
 * RoundCoordinator emits after the OnAttack window (the Buff Attack
 * convention, mirrored for the enemy side).
 */
export const BLUNT_ATK_REDUCTION = 1;

// ─── Mark (Blightcaster) — level requirement 1 ────────────────────────────

/** Stable ability ID for Mark. */
export const MARK_ABILITY_ID = 'mark';

/**
 * Extra damage a MARKED copy takes on EVERY damage instance it takes (the
 * mark is part of the damage instance itself, so the Holy Shield halving
 * applies to the marked total — an 8-ATK Undead hit on a marked Holy Shield
 * copy deals ceil((8 + 3) / 2) = 6). Combat-scoped: the flag lives on the
 * combat copy (copies are rebuilt every round, so the mark dies with the
 * combat). The executor (OnAttack — see markExecutor in GameAbilities) sets
 * it on the defender it attacked (living defenders only); the +3 is applied
 * in CombatMinionCopy.takeDamage — the single damage choke point — so the
 * mark works against ANY damage source (exchange, splash, blast, sudden
 * death, the Toxic Cloud tick, Shared Pain shares). The RoundCoordinator
 * streams a 'mark' combat event when the defender newly gains the mark
 * (the client's continuous black-particle emitter at the marked minion).
 */
export const MARK_EXTRA_DAMAGE = 3;

// ─── Shared Pain (Blightcaster) — level requirement 4 ─────────────────────

/** Stable ability ID for Shared Pain. */
export const SHARED_PAIN_ABILITY_ID = 'shared_pain';

/**
 * ACQUISITION: only learnable by a minion that ALREADY has the Mark ability
 * (the one-way gate in isAbilityEligibleForUpgrade — unlike the replacement
 * abilities, Mark is NOT removed, so the holder marks AND shares).
 *
 * EFFECT: every time the holder TAKES damage (the HP actually lost —
 * overkill not absorbed; hits converted by Blessed or negated by Second
 * Chance are not damage taken and share nothing), EVERY LIVING ENEMY MARKED
 * copy takes that same amount (the mark's +3 applies to the share too — it
 * is damage). The re-entrancy guard (CombatMinionCopy.takeDamage
 * fromSharedPain option) keeps a target's own Shared Pain from re-firing on
 * the shared damage it just took — one propagation level per damage
 * instance, so two mutually marked holders cannot ping-pong forever.
 *
 * Accumulation lives on the combat copy (pendingSharedPain — takeDamage
 * queues the actual HP loss when the copy carries Shared Pain); the
 * RoundCoordinator drains it at every damage site (exchange, OnAttack
 * window, OnDeath window, the explosion-chain blasts, the sudden-death
 * tick, the Toxic Cloud tick) and streams a 'sharedPain' event (the client's
 * red expanding particle line from the holder to every marked victim) + the
 * simultaneous damage/death events. Ability-damage convention: per-user kill
 * credit for enemy non-token victims + kill gold for the kills via the
 * centralized death-gold rule (RoundCoordinator._awardDeathGold — the
 * share is the enemy side's doing, so each kill pays the holder's owner).
 */

// ─── Frenzy (Runekeeper) — level requirement 1 ─────────────────────────────

/** Stable ability ID for Frenzy. */
export const FRENZY_ABILITY_ID = 'frenzy';

/**
 * ACQUISITION: the Runekeeper class (level requirement 1).
 *
 * EFFECT: a combat aura on the holder itself — while the holder's CURRENT
 * HP is strictly below HALF of its max HP (initialHp), it attacks with
 * FRENZY_ATK_BONUS (15) extra attack. The condition is re-evaluated at the
 * start of every attack turn (the RoundCoordinator.updateFrenzyAura
 * recompute, the same convention as the Warband aura): a holder that drops
 * below the halfway point gains the bonus, one that is healed back above
 * loses it again. Like every combat buff it is TEMPORARY — combat copies
 * only, never permanentStatChanges (does not persist after battle).
 */
export const FRENZY_ATK_BONUS = 15;

// ─── Random Evolution (Runekeeper) — level requirement 1 ───────────────────

/** Stable ability ID for Random Evolution. */
export const RANDOM_EVOLUTION_ABILITY_ID = 'random_evolution';

/**
 * ACQUISITION: the Runekeeper class (level requirement 1). LEARNING IT
 * NEVER ADDS 'random_evolution' to the minion's ability list — the upgrade
 * resolves to a RANDOM KEYWORD from any class instead (see
 * randomEvolutionPool / resolveRandomEvolutionGrant in UpgradePath), so the
 * option can be learned MULTIPLE times (the "already owned" filter never
 * sees it).
 *
 * OFFER RULE (the shared getEligibleAbilityUpgrades fallback): offered ONLY
 * when there is NO OTHER eligible ability for the minion — it is the
 * last-resort keyword option, never shown alongside a normal choice. It is
 * re-offered on later upgrades as long as that remains true and the grant
 * pool is not empty.
 *
 * THE GRANT: a uniform random pick from randomEvolutionPool — every ability
 * defined in the game (ANY class), level-gated (availableAtLevel <= the
 * minion's level — the grant is always "in the pool of available
 * abilities"), excluding the abilities the minion already has and the meta
 * board-rule abilities (Grandmaster / Second Chance — not keywords). The
 * pick is resolved SERVER-side with the authoritative RNG and LOCKED with
 * the same pre-selection convention as the normal ability option (a re-open
 * of the upgrade menu never re-rolls it; the lock releases when the minion
 * is upgraded).
 *
 * IN COMBAT it is inert (the keyword it granted is what fights — the
 * executor is a no-op registered for completeness).
 */

// ─── Buffer kit: Strengthen + Blessing ────────────────────────────────────
// Two combat auras of the locked Buffer class (the class reaches the board
// via the transform system). Both are "while this minion is alive" auras
// following the Warband convention exactly: the effect lives in the
// RoundCoordinator (recomputed at combat start and before every attack),
// the executors are no-ops, the bonus is tracked per copy so it can be
// partially removed when one of several holders dies, and everything is
// TEMPORARY by construction (combat copies only, never
// permanentStatChanges — nothing persists to the battlefield after combat).

// ─── Strengthen (Buffer) — level requirement 1 ────────────────────────────

/** Stable ability ID for Strengthen. */
export const STRENGTHEN_ABILITY_ID = 'strengthen';

/**
 * Stat bonus granted to EVERY LIVING friendly combat copy (including the
 * Strengthen holders themselves) while at least one Strengthen holder is
 * alive: +STRENGTHEN_STAT_BONUS attack AND +STRENGTHEN_STAT_BONUS HP.
 * The holders STACK (two living Strengthen minions = +6/+6 — the Warband
 * per-copy stacking convention). Combat-only: the aura is recomputed
 * before every attack (RoundCoordinator.updateStrengthenAura), so the bonus
 * drops when the last holder dies and is restored if one is resurrected.
 * Removal rules: the ATK drop is floored at 0 (Warband convention) and the
 * HP drop lowers current HP AND max HP but can NEVER kill a copy (current
 * HP is floored at 1 — aura removal is not a damage source). A copy with
 * Grandmaster gains the ATK portion but never the HP portion (the
 * Grandmaster "never gains HP" convention — the same guard Buff Vitality,
 * the Health-rune buff and SoulLink use).
 */
export const STRENGTHEN_STAT_BONUS = 3;

// ─── Blessing (Buffer) — level requirement 1 ──────────────────────────────

/** Stable ability ID for Blessing. */
export const BLESSING_ABILITY_ID = 'blessing';

/**
 * Flat lifesteal heal granted to EVERY friendly attacking copy (including
 * the Blessing holders themselves) on EACH attack while at least one
 * Blessing holder is ALIVE on the attacker's side. The holders STACK
 * (two living Blessing minions = +4 — the Warband per-copy stacking
 * convention); the count is read live at attack time, so a Blessing minion
 * that died before the attack simply stops granting ("if this minion dies
 * the effect is removed" — no per-copy tracking needed, unlike the stat
 * auras).
 *
 * NO RESURRECTION (the Lifesteal convention): the heal is applied after the
 * exchange's simultaneous damage and only to a SURVIVING attacker — a copy
 * the exchange dropped to 0 HP stays dead (a 2-HP minion that attacks a
 * 10-ATK minion is 2 - 10 + 2 = -6 -> dead, the +2 does not bring it back).
 * An attacker that already has the Lifesteal ability gains the bonus ON TOP
 * of its damage-based heal (both are capped at the copy's initial HP, so
 * the combined heal can never exceed max HP). The heal is an ability HP
 * effect on the RECIPIENT: a Fortress recipient doubles it and the
 * attacker's owner's Rune of Healing doubles it again (multiplicative —
 * the exact Lifesteal multipliers). The effect lives in
 * RoundCoordinator.applyBlessingLifesteal (right after the OnAttack window,
 * before the window's HP-diff scan, so the heal streams as the usual
 * heal-style damage event); the executor is a no-op.
 */
export const BLESSING_LIFESTEAL_BONUS = 2;

// ─── Looter (Warrior, Runekeeper) — level requirement 5 ────────────────────

/** Stable ability ID for Looter. */
export const LOOTER_ABILITY_ID = 'looter';

/**
 * ACQUISITION: the Warrior and Runekeeper classes (level requirement 5).
 *
 * EFFECT (the centralized death-gold rule — see RoundCoordinator.
 * _awardDeathGold): every ENEMY minion that dies in combat awards its
 * level in kill gold to the player who killed it (the victim's enemy —
 * no matter how it died: exchange, explosion/Exploding blast, Toxic Cloud,
 * Blast splash, Shared Pain, the sudden-death tick). Each LIVING Looter on
 * the RECIPIENT's side adds LOOTER_GOLD_PER_DEATH (1) additional gold per
 * such enemy death — the holders STACK (two living Looters = +2, the
 * Warband per-copy stacking convention) and the count is read live at
 * death time, so a Looter that died this combat stops granting ("while
 * this minion is alive" — no per-copy tracking needed, like Blessing).
 * Deaths caused by the victim's OWN side (friendly fire — e.g. an Exploding
 * minion that detonates its own board) pay NOBODY, and so does a Looter.
 * Necromancy/Rune-of-the-Dead tokens are not cards and never pay.
 *
 * The executor is a no-op (the passive/aura convention — the effect lives
 * in the RoundCoordinator's death-gold sites, which all funnel through
 * _awardDeathGold).
 */
export const LOOTER_GOLD_PER_DEATH = 1;

// ─── Brute (Warrior, Mage) — level requirement 2 (Tank no longer offered) ─

/** Stable ability ID for Brute. */
export const BRUTE_ABILITY_ID = 'brute';

/**
 * ACQUISITION: the Warrior, Mage and Tank classes (level requirement 2).
 *
 * EFFECT: every time the holder ATTACKS (as the attacker of an exchange —
 * counter-attacks never trigger it), its attack is DOUBLED and its HP is
 * HALVED — applied BEFORE the exchange's simultaneous damage, so the
 * doubled attack deals the hit and the halved HP takes the counter-attack
 * (10/10 → 20/5 on the first attack, 40/2 on the second, 80/1 on the
 * third, ...). The halving affects CURRENT HP AND the max-HP/heal cap
 * (initialHp — the established in-combat max-HP cast idiom) and can never
 * reduce the HP below BRUTE_HP_FLOOR (1). Each attack re-triggers on the
 * CURRENT stats (the effect compounds while it lasts).
 *
 * TEMPORARY by construction: the change is applied to the combat COPY only
 * (never to permanentStatChanges — nothing persists to the battlefield
 * after combat; copies are rebuilt every round). The mid-combat stat change
 * streams as ONE 'aura' event (attack + hp + maxHp — the Strengthen
 * shape), emitted right after the attack event and before the exchange's
 * damage event, so the client updates the ATK number and the HP bar
 * exactly when the attack happens.
 *
 * The effect lives in RoundCoordinator._applyBruteOnAttack (called in
 * runCombatToCompletion after the 'attack' event, before
 * applySimultaneousDamage); the executor is a no-op.
 */
export const BRUTE_HP_FLOOR = 1;

// ─── Freeze (Mage, Bard) — level requirement 4 (task 221) ─────────────────

/** Stable ability ID for Freeze. */
export const FREEZE_ABILITY_ID = 'freeze';

/**
 * ACQUISITION: the Mage and Bard classes (level requirement 4 — task 221
 * raised it from 3 to match the reworked effect).
 *
 * EFFECT (task 221): every attack the holder makes rolls FREEZE_TARGET_
 * CHANCE (75%) to FREEZE the TARGET, otherwise FREEZE_NEIGHBOR_CHANCE
 * (25%) to FREEZE ONE of the target's NEIGHBORS (a random living, unfrozen
 * neighbor — the roll fizzles when the target has no living neighbors).
 * A frozen minion is unfrozen at the moment it would have attacked and
 * SKIPS that attack (no damage, no OnAttack abilities, no
 * attack-count for sudden death — the sequence simply moves on to the next
 * minion). The freeze is per-combat: the flag lives on the combat copy
 * (frozen) and resets automatically when copies are rebuilt each round.
 * Re-freezing an already frozen copy is a no-op (it skips exactly one
 * attack); a frozen minion can be damaged and killed like any other.
 * The roll uses the seeded match RNG (deterministic per match seed), and
 * the effect lives in the RoundCoordinator (applyFreeze after the exchange
 * damage event; the skip in runCombatToCompletion's attacker selection) —
 * the executor is a no-op (the passive convention, like Warband/Brute).
 */
export const FREEZE_TARGET_CHANCE = 0.75;
export const FREEZE_NEIGHBOR_CHANCE = 0.25;

// ─── Swarmer class + Glasscannon / SwarmLord (Task: new Swarmer class) ────
//
// The Swarmer is a LOCKED class (the fifth locked one, after
// Blightcaster/Buffer/Runekeeper): its definition (SWARMER_MINION_DEFINITION
// in testMinions.ts) is exported but NOT part of the `testMinions` base
// draw pool and it has NO draw/buy/summon/replacement source — it can
// never be drawn, bought or played. It reaches the board ONLY as a
// TEMPORARY COMBAT TOKEN: the SwarmLord ability (below) fills the owner's
// board with Swarmers when the holder dies, and they are discarded when
// combat ends (battlefield restoration — the Necromancy-Undead token
// convention: combat copies + order array only, never persistent state).
//
//   Swarmer: 1 HP / 10 ATK, brown class color, starts with Glasscannon.
//
// Token conventions (identical to the Necromancy Undead token):
//  - instance IDs carry the `swarmer_` prefix (see isCombatTokenInstanceId
//    in CombatCopy.ts);
//  - they are never in the owner's persistent minionInstances map (so the
//    Exploding/Grief/Resurrect scans exclude them automatically);
//  - they pay NO kill gold when they die (the double-pay guard — the
//    SwarmLord holder's death already paid) and never count for the
//    per-user kill statistic (minionKilledHandler);
//  - they never consume the Rune of the Dead / Rune of Rebirth charges
//    (firstDeadNonTokenCopy skips them).

/** Stable base minion ID of the Swarmer. */
export const SWARMER_MINION_ID = 'swarmer';

/** Base HP of a Swarmer (1 — it dies after its first attack anyway). */
export const SWARMER_HP = 1;

/** Base attack of a Swarmer. */
export const SWARMER_ATTACK = 10;

// TASK 204: the SPAWNER unit (SPAWNER_MINION_ID / _HP / _ATTACK) was
// REMOVED — it was an accidental class (a Swarmer misspelling) and never
// meant to exist. The Infestation spell's combat token IS the Swarmer
// (a Fragile Swarmer — the SWARMER_* constants above, the `infestation_`
// token-ID prefix, the token conventions of the Swarmer / Necromancy
// Undead: no persistent map, no kill gold, discarded at combat end, no
// chains).

// ─── Glasscannon (Swarmer) — level requirement 0 ───────────────────────────

/** Stable ability ID for Glasscannon. */
export const GLASSCANNON_ABILITY_ID = 'glasscannon';

/**
 * ACQUISITION: the Swarmer class only — it is the SWARMER_MINION_DEFINITION
 * base ability (testMinions.ts); the definition's `types` is exactly
 * ['swarmer'], so no other class can ever be offered it.
 *
 * EFFECT: "this minion dies after it has attacked." A REAL OnAttack
 * executor: when the holder is the attacker of an exchange, after the
 * exchange's simultaneous damage has landed the holder's combat copy is
 * killed (its HP set to 0). A copy the exchange already killed (a lethal
 * counter-attack) is a corpse already — the executor is a no-op for it.
 * The death is then finalized by the normal per-exchange death pipeline
 * (death event; kill gold / kill credit are excluded by the token rules —
 * see the Swarmer header above; Glasscannon has no OnDeath effects, so the
 * OnDeath window is empty for a Swarmer).
 */

// ─── SwarmLord (Mage, Blightcaster) — level requirement 5 ──────────────────

/** Stable ability ID for SwarmLord. */
export const SWARMLORD_ABILITY_ID = 'swarmlord';

/**
 * ACQUISITION: the Mage and Blightcaster classes (level requirement 5 —
 * lowered from 6: the swarm kit opens one level earlier).
 *
 * EFFECT: "when this minion dies it fills your board with Swarmers that
 * all attack immediately." When a SwarmLord holder DIES in combat (any
 * death source — exchange, ability damage, sudden death), AFTER all of the
 * exchange's death effects are fully resolved (OnDeath windows, the
 * explosion chain, SoulLink, the rune death effects, Necromancy, the grief
 * passes, the deferred Resurrects — "all on-deaths are resolved first"),
 * the holder's board is filled with fresh LEVEL-1 Swarmer tokens
 * (10 ATK / 1 HP, Glasscannon) — ONE per empty battlefield slot (a slot
 * with no LIVING copy, the holder's corpse slot included — the board ends
 * FULL, at GameConfig.maxBoardSize), placed in the empty slots in
 * left-to-right order. Immediately after, ALL of the freshly spawned
 * Swarmers attack, one after another in position order (left to right),
 * BEFORE the opposing side's next minion takes its turn: each Swarmer runs
 * a full attack exchange (targeting with Shield priority + seeded RNG,
 * simultaneous damage, the death pipeline for every death it causes —
 * Explosive/Exploding chain, SoulLink, rune death effects, Necromancy,
 * kill gold and kill credit for ENEMY cards it kills), and then dies of
 * its own Glasscannon. A Swarmer's own death is a token death (no kill
 * gold, no kill credit, no rune charges — see the Swarmer header), so the
 * burst is always bounded: every Swarmer attacks exactly once.
 *
 * The burst is a SPECIAL RESOLUTION, not a regular turn: it runs no
 * Toxic Cloud tick and no sudden-death tick (per-turn / per-attack-count
 * mechanics — the burst is bounded by construction, each Swarmer
 * self-destructs after its attack), and it does not count in the
 * sudden-death attack counter. A burst exchange that kills ANOTHER SwarmLord
 * holder (e.g. via the defender's OnAttack splash) queues a FURTHER burst
 * for that holder (per-death trigger — the same convention as every other
 * death effect; the chain terminates because a board full of corpses
 * leaves no empty slots to fill).
 *
 * The executor is a no-op (the passive convention — the effect lives in
 * the RoundCoordinator's _resolveSwarmLordBursts, called at the end of
 * every attack exchange, just before the outcome check).
 */

// ─── SwarmNest (Mage, Blightcaster) — level requirement 6 ─────────────────

/** Stable ability ID for SwarmNest. */
export const SWARMNEST_ABILITY_ID = 'swarmnest';

/**
 * ACQUISITION: the Mage and Blightcaster classes (level requirement 6 —
 * the base maximum level). ONE-WAY GATE (the Shared Pain / Mark pattern,
 * in isAbilityEligibleForUpgrade): it is offered ONLY to a minion that
 * ALREADY has SwarmLord — and SwarmLord is NOT removed (the holder keeps
 * swarming while its Swarmers change kit).
 *
 * EFFECT: "your Swarmers spawn without the Fragile keyword." A SwarmLord
 * burst that runs from THIS holder spawns Swarmer tokens WITHOUT the
 * Fragile (Glasscannon) base ability — the Swarmers survive their one
 * burst attack (1 HP, no self-destruction) and sit on the board as
 * ordinary 1-HP minions for the rest of the combat (the burst is a
 * special resolution — they never re-attack in the main sequence; they
 * are discarded with every other combat token when the combat ends).
 * Only the holder's OWN burst is modified (the owner is the holder's
 * player — the Rune of Decay's summons are the rune's, not the holder's,
 * and keep Fragile). The effect lives in the RoundCoordinator's swarmer
 * spawn (_runSwarmLordBurst reads the holder's abilities); the executor
 * is a no-op (the passive convention).
 */

// ─── Item class (Task: Items) ────────────────────────────────────────────────
//
// The ITEM class: items are class cards that CAN sit on the battlefield
// (a future acquisition path will place them — today they are excluded
// from the draw pool like the locked classes, with no draw/buy/summon/
// replacement source). Their class rules (enforced authoritatively):
//  - they NEVER attack: the RoundCoordinator's attacker loop always skips
//    an item's turn (no damage, no OnAttack dispatch, no sudden-death
//    attack count — the freeze-skip location), and a combat where NEITHER
//    side has a living non-item minion ends immediately (the post-loop
//    outcome code marks it a tie — without the guard the all-item boards
//    would loop forever, since the sudden-death tick fires per attack);
//  - they CAN deal COUNTER damage like regular minions (the exchange's
//    simultaneous defender retaliation reads the item's attack — a
//    5-ATK Life Chest kills a 5-HP attacker);
//  - they are ALWAYS level 1 and can NEVER be upgraded (the server offers
//    no upgrade options for them and GameSessionService.upgradeMinion
//    rejects every branch on an item — the bots' branch builder returns
//    [] too);
//  - they CAN carry keywords (base abilities — Treasure, Life-full,
//    Explosive on the Exploding Barrel);
//  - they render with the light yellow class color and a PER-CARD image
//    (each item has its own fixed art keyed by minion ID in MinionCard.ts
//    — the user cannot change an item's skin); they have NO SkinDefinition
//    entries, so they appear in NO skin menu.
//
// The three items (definitions in testMinions.ts):
//   Treasure Chest:   25 HP / 0 ATK, starts with Treasure.
//   Life Chest:       25 HP / 5 ATK, starts with Life-full.
//   Exploding Barrel: 12 HP / 0 ATK, starts with Explosive.

/** The Items minion class (minion type). */
export const ITEM_MINION_TYPE = 'item';

// ─── Treasure Chest (Items) ─────────────────────────────────────────────────

/** Stable base minion ID of the Treasure Chest. */
export const TREASURE_CHEST_MINION_ID = 'treasure_chest';

/** Starting HP of a Treasure Chest (25). */
export const TREASURE_CHEST_HP = 25;

/** Starting attack of a Treasure Chest (0 — items never attack anyway). */
export const TREASURE_CHEST_ATTACK = 0;

// ─── Life Chest (Items) ─────────────────────────────────────────────────────

/** Stable base minion ID of the Life Chest. */
export const LIFE_CHEST_MINION_ID = 'life_chest';

/** Starting HP of a Life Chest (25). */
export const LIFE_CHEST_HP = 25;

/** Starting attack of a Life Chest (5 — counter damage only; it never attacks). */
export const LIFE_CHEST_ATTACK = 5;

// ─── Exploding Barrel (Items) ───────────────────────────────────────────────

/** Stable base minion ID of the Exploding Barrel. */
export const EXPLODING_BARREL_MINION_ID = 'exploding_barrel';

/** Starting HP of an Exploding Barrel (12). */
export const EXPLODING_BARREL_HP = 12;

/** Starting attack of an Exploding Barrel (0 — items never attack anyway). */
export const EXPLODING_BARREL_ATTACK = 0;

// ─── Treasure (Items) — level requirement 0 ─────────────────────────────────

/** Stable ability ID for Treasure. */
export const TREASURE_ABILITY_ID = 'treasure';

/**
 * ACQUISITION: the Items class only — it is the TREASURE_CHEST_MINION_DEFINITION
 * base ability (testMinions.ts); the definition's `types` is exactly
 * ['item'], so no other class can ever be offered it.
 *
 * EFFECT: "gives 25 gold to the player who killed this item." Folded into
 * the CENTRALIZED DEATH-GOLD RULE (RoundCoordinator._awardDeathGold): a
 * Treasure-carrying item's death pays the victim's ENEMY (the killer)
 * calculateKillGold(level) + TREASURE_GOLD_REWARD (25) in the SAME killGold
 * event (the client's "+x gold" float shows the total). Every death source
 * benefits automatically (exchange, blasts, splash, the sudden-death tick),
 * and the friendly-fire rule applies unchanged: a death caused by the
 * victim's OWN side pays NOBODY (no treasure gold either).
 *
 * The executor is a no-op (the passive convention — the effect lives in
 * the RoundCoordinator's death-gold engine).
 */
export const TREASURE_GOLD_REWARD = 25;

// ─── Life-full (Items) — level requirement 0 ────────────────────────────────

/** Stable ability ID for Life-full. */
export const LIFE_FULL_ABILITY_ID = 'life_full';

/**
 * ACQUISITION: the Items class only — it is the LIFE_CHEST_MINION_DEFINITION
 * base ability (testMinions.ts); the definition's `types` is exactly
 * ['item'], so no other class can ever be offered it.
 *
 * EFFECT: "instantly gives 1 extra life to the player who killed this
 * item." Applied the moment the item dies, in the centralized death-gold
 * engine (RoundCoordinator._awardDeathGold): the victim's ENEMY (the
 * killer — the same recipient the kill gold goes to) gains
 * LIFE_FULL_LIFE_BONUS (1) life on its persistent PlayerState (the
 * post-combat GameStateUpdate carries the new life). The friendly-fire
 * rule applies unchanged: a death caused by the victim's own side grants
 * nobody life.
 *
 * The executor is a no-op (the passive convention — the effect lives in
 * the RoundCoordinator's death-gold engine).
 */
export const LIFE_FULL_LIFE_BONUS = 1;

// ─── Spellmaker (Spellmaker class) — level requirement 1 ───────────────────

/** Stable ability ID for the Spellmaker keyword. */
export const SPELLMAKER_ABILITY_ID = 'spellmaker';

/**
 * ACQUISITION: the Spellmaker class only — it is the
 * SPELLMAKER_MINION_DEFINITION base ability (testMinions.ts); the
 * definition's `types` is exactly ['spellmaker'], so no other class can
 * ever be offered it.
 *
 * EFFECT: "When on your battlefield, adds spells to your draw pile; if
 * spells are already in your pile, it improves the spells in your pile."
 * A Spellmaker on the owner's battlefield is ONE spell source:
 *  - the owner's spell draw POWER is 2×(#SpellMasters) + 1×(#Spellmakers)
 *    on the battlefield, plus 1 more while the owner (or, in COOP, the
 *    owner's teammate) has the Rune of the Mage — the sources STACK;
 *  - with power N ≥ 1 every card drawn has a chance N × the single-source
 *    chance (N/(poolSize+1) — for N = 1 that is exactly the Rune of the
 *    Mage's "equal to any specific class" odds) to be a spell;
 *  - the drawn spell is a RANDOM spell of level ≤ N (N = 1: level-1
 *    spells; N = 2: level 1-2 spells; N = 3: level 1-3 spells; ...).
 * The effect lives in the draw services (CardDrawService — the round draw
 * in RoundCoordinator and the purchase draw in GameSessionService); the
 * executor is a no-op (the passive convention).
 */
export const SPELLMAKER_SPELL_POWER = 1;

// ─── SpellMaster (Spellmaker class) — level requirement 5 ──────────────────

/** Stable ability ID for the SpellMaster keyword. */
export const SPELLMASTER_ABILITY_ID = 'spellmaster';

/**
 * ACQUISITION: the Spellmaker class only, at level 5. ONE-WAY REPLACEMENT:
 * it REPLACES the Spellmaker ability — a SpellMaster holder can only be
 * offered it while it has Spellmaker, the upgrade removes Spellmaker
 * (abilitiesReplacedByUpgrade), and Spellmaker is never re-offered to a
 * SpellMaster holder (the Greater Heal / Heal pattern).
 *
 * EFFECT: the same as Spellmaker, but it COUNTS TWICE: a SpellMaster is
 * spell power 2 (two spell sources) — it lets its owner draw level-2
 * spells at 2× the single-source chance directly, and it stacks with
 * other Spellmakers / SpellMasters / the Rune of the Mage (see
 * SPELLMAKER_ABILITY_ID for the full rule).
 *
 * The executor is a no-op (the passive convention — the draw services do
 * the work).
 */
export const SPELLMASTER_SPELL_POWER = 2;

// ─── Spellburst / SpellBlast (Spellmaker, Runekeeper) ─────────────────────

/**
 * Stable ability ID for the SPELLBURST keyword (Spellmaker / Runekeeper,
 * level requirement 5): when the holder DIES, the owner receives a random
 * LEVEL-1 spell in their hand (the draw conventions: a full hand loses
 * the card; the roll uses the match's seeded RNG and the instance id is
 * seed-derived — deterministic per seed). The effect lives in the OnDeath
 * executor (GameAbilities); the granted spell is a real spell card (cast,
 * sell, give — the full spell lifecycle).
 */
export const SPELLBURST_ABILITY_ID = 'spellburst';

/**
 * Stable ability ID for the SPELLBLAST keyword (Spellmaker / Runekeeper,
 * level requirement 6): like Spellburst, but the death grants a random
 * spell of ANY level (the full catalogue). ONE-WAY REPLACEMENT: it
 * REPLACES Spellburst — a holder can only be offered it while it has
 * Spellburst, the upgrade removes Spellburst (abilitiesReplacedByUpgrade),
 * and Spellburst is never re-offered to a SpellBlast holder (the Greater
 * Heal / Heal pattern).
 */
export const SPELLBLAST_ABILITY_ID = 'spellblast';

// ─── Spell-granted keywords (Task: New Spells) ─────────────────────────────
//
// The new spell cards (SpellDefinitions) grant these keywords to existing
// battlefield minions. They are CATALOG-ONLY (`types: []` in
// GAME_ABILITY_DEFINITIONS — no class can acquire them through an upgrade;
// the spell is the sole source), so the minion's card renders the keyword
// and the combat engine reads the marker from the persistent instance.

/**
 * Stable ability ID for the TREASURE HUNTER keyword (spell-granted, level-2
 * spell): the first time the holder KILLS an enemy minion in combat, a
 * golden treasure (the Treasure Chest item) spawns on the ENEMY side at the
 * position of the killed minion (it belongs to the enemy — the victim's
 * owner). The effect lives in the RoundCoordinator: the Phase-3 kill
 * detection records the spawn on the session, and the post-combat step
 * inserts the item into the victim's board. The per-combat charge is the
 * combat copy's `treasureHunterUsed` flag (the hasKilledInCombat
 * convention). The executor is a no-op (the passive convention).
 */
export const TREASURE_HUNTER_ABILITY_ID = 'treasure_hunter';

/**
 * Stable ability ID for the DIVINE BLESSING keyword (spell-granted, level-1
 * spell): the FIRST positive damage the holder takes in combat is IGNORED
 * entirely (no HP lost, the damage instance never lands — the whole
 * instance, Mark included, is negated; the charge is per combat, the
 * combat-copy convention like Blessed / Second Chance). The effect lives in
 * CombatMinionCopy.takeDamage (the single damage choke point) — the first
 * check, before Mark / Holy Shield / Blessed / Second Chance — and streams
 * as an 'ability' event through pendingAbilityTriggers (the client's large
 * yellow explosion on the minion). The executor is a no-op.
 */
export const DIVINE_BLESSING_ABILITY_ID = 'divine_blessing';

/**
 * Stable ability ID for the INFESTED keyword (spell-granted, level-2 spell).
 * The spell no longer grants this keyword itself: it grants the SWARMLORD
 * keyword IMMEDIATELY (temporarily — marked with minion.infestedSwarmLord),
 * which lasts ONE combat only — the RoundCoordinator records the holder at
 * session creation (_recordInfestedHolders) and the post-combat step removes
 * the SWARMLORD keyword (the ONE-COMBAT keyword convention — the MINION
 * STAYS on the board, un-removed and un-sold; a class-granted SwarmLord is
 * never touched). A player who sits out keeps the keyword for the next
 * combat they take part in (the healingRainArmed convention). The ID stays
 * in the catalogue for stability; the executor is a no-op.
 */
export const INFESTED_ABILITY_ID = 'infested';

/**
 * REPEATER (Spellmaker, level 3 — task 184): at the START OF COMBAT, the
 * holder re-casts the last minion-targeting spell that was cast ON THE
 * HOLDER (MinionInstance.lastCastSpellId) on BOTH of its battlefield
 * neighbors (the spell's full effect: permanent stats/keywords/counters —
 * e.g. an Upgraded last cast escalates the caster's ladder, a Protector
 * shields both neighbors). The effect lives in the RoundCoordinator's
 * applyRepeaterCasts (top of runCombatToCompletion — it needs the session
 * for the Infested removal record); the executor is a no-op (the passive
 * convention).
 */
export const REPEATER_ABILITY_ID = 'repeater';

// ─── Task 192: LastWish + Swift ────────────────────────────────────────────

/**
 * Stable ability ID for LASTWISH (Buffer, level requirement 3):
 * "When this minion dies, all living minions on its side get +5 HP and
 * +5 attack for the rest of this combat." The buff is COMBAT-SCOPED
 * (the combat copies only — the battlefield restores from the pre-combat
 * snapshot after combat, exactly like every other in-combat buff); it
 * hits every LIVING friendly copy at the moment of the death (the dead
 * holder is skipped; a copy that dies later in the same combat still kept
 * the buff while it lived). Combat deaths only — a prep-phase death
 * (High Stakes detonation, the Sacrifice spell) has no "this combat".
 */
export const LASTWISH_ABILITY_ID = 'last_wish';

/** LastWish: the +N/+N granted to every living friendly minion. */
export const LASTWISH_STAT_BONUS = 5;

/**
 * Stable ability ID for SWIFT (Warrior/Blightcaster/Mage, level
 * requirement 3): "This minion attacks twice" — on its turn the holder
 * makes a FULL first attack (targeting, damage, every window, death
 * effects — everything resolves), and when that turn is over, if it is
 * STILL ALIVE, it takes a second turn immediately and attacks again
 * (a full new exchange: fresh target selection, fresh damage, ...). The
 * re-attack is budgeted to ONE per turn (CombatMinionCopy.
 * swiftChargesLeft) — two attacks per turn, never a loop. The effect
 * lives in the RoundCoordinator's combat loop + CombatSequence.
 * queueImmediateReattack (the executor is a no-op — the double attack is
 * a sequence behavior, not an ability dispatch).
 */
export const SWIFT_ABILITY_ID = 'swift';

// ─── New-spell effect values ────────────────────────────────────────────────

/**
 * ACID RAIN (spell, battlefield, level 2): the damage EVERY LIVING minion on
 * BOTH sides takes whenever ANY minion attacks (one damage instance per
 * victim per attack — the RoundCoordinator's applyAcidRain, called at the
 * two attack exchange sites after the exchange's damage pipeline). Routes
 * through takeDamage (the single damage choke point: Mark / Blessed / Second
 * Chance / Shared Pain all interact); the victim's ENEMY side's caster earns
 * kill gold for its kills (a death caused only by the victim's OWN side's
 * rain is friendly fire and pays nobody — the caller-side guard).
 */
export const ACID_RAIN_DAMAGE = 1;

/**
 * ACID RAIN duration: the number of ATTACKS the effect lasts ("lasts for
 * 4 attacks" — a cast adds ACID_RAIN_ATTACK_COUNT attacks to the caster's
 * `acidRainAttacksLeft` counter; each time the rain triggers — ANY minion
 * attacks while the caster's side is armed — it consumes exactly 1, so the
 * 4 charges can span several combats). A side with no remaining attacks is
 * not armed for a combat.
 */
export const ACID_RAIN_ATTACK_COUNT = 4;

/**
 * BLIZZARD (spell, battlefield, level 2): for the caster's NEXT combat only,
 * BEFORE every attack there is a BLIZZARD_FREEZE_CHANCE (25%) that the
 * ATTACKING minion is FROZEN INSTEAD of attacking. A blizzard-frozen copy is
 * frozen for BLIZZARD_FREEZE_ATTACKS (2) ATTACKS — it skips its next two
 * attacks (the copy's `blizzardSkipsLeft` counter: each would-be attack
 * unfreezes-one-step (freezeSkip event) and re-freezes while skips remain,
 * so the client's ice overlay stays on across both skips; at zero it is
 * fully unfrozen and attacks normally, with a fresh roll). The Freeze
 * KEYWORD's freeze stays one attack (its own `frozen` flag, no counter).
 * The roll uses the seeded match RNG (one float per attack, deterministic
 * per seed); the effect is armed into the combat session from the caster's
 * `blizzardArmed` flag and consumed there. Streams as the existing 'freeze'
 * event (the client's ice burst) + 'freezeSkip' (the shatter) + the
 * combat-wide snow FX (CombatStarted.blizzard).
 */
export const BLIZZARD_FREEZE_CHANCE = 0.25;

/** BLIZZARD freeze DURATION: the number of ATTACKS a blizzard-frozen copy skips. */
export const BLIZZARD_FREEZE_ATTACKS = 2;

/**
 * UPGRADED (spell, minion, level 2): the PERMANENT +N/+N granted to the
 * target minion (the Power Touch convention — gainHp clamps at the
 * Grandmaster cap, a Teacher target redirects the gain to its neighbors via
 * redirectTeacherStatGain).
 */
export const UPGRADED_STAT_BONUS = 8;

/**
 * UPGRADED (spell, task 183): the ADDITIONAL +N/+N gained per previous cast
 * of the spell BY THE SAME PLAYER (per-player, not global): 1st cast
 * +8/+8, 2nd +12/+12, 3rd +16/+16, … (UPGRADED_STAT_BONUS +
 * UPGRADED_SPELL_STEP_BONUS × castsSoFar). The counter lives on
 * PlayerState.upgradedSpellCasts.
 */
export const UPGRADED_SPELL_STEP_BONUS = 4;

/**
 * IMPROVED (spell, battlefield, level 1): advances the caster's match-wide
 * Improved counter by 2 — exactly as if two Undead-class units had been
 * played (the registerUndeadPlayed convention: every minion with Improved —
 * in hand/board and in combat — gains the derived +1/+1 per count from the
 * purchase on). The counter is match-wide and monotonic, so the +2
 * persists for the rest of the game.
 */
export const IMPROVED_SPELL_BONUS = 2;
